import { theme } from '@endure/ui';
import {
  type Locale,
  RainbowKitProvider,
  type Theme,
  darkTheme,
  useConnectModal,
} from '@rainbow-me/rainbowkit';
import { reconnect as wagmiReconnect } from '@wagmi/core';
import { merge } from 'lodash-es';
import { type PropsWithChildren, useEffect, useRef } from 'react';
import { useAccount, useConfig } from 'wagmi';

import '@rainbow-me/rainbowkit/styles.css';
import { useTranslation } from 'libs/translations';

export interface RainwbowKitWrapperProps extends PropsWithChildren {}

const rkTheme = merge(
  darkTheme({
    accentColor: theme.colors['forge-light-accent'],
    borderRadius: 'small',
  }),
  {
    fonts: {
      body: '"ProximaNova", var(--font-fallback)',
    },
    colors: {
      closeButtonBackground: 'transparent',
      generalBorder: theme.colors['dark-blue-hover'],
      selectedOptionBorder: theme.colors['dark-blue-hover'],
      modalBorder: theme.colors['forge-light-accent'],
      modalBackground: theme.colors['dark-blue'],
    },
  } as Theme,
);

const ConnectionRecovery: React.FC = () => {
  const config = useConfig();
  const { connectModalOpen } = useConnectModal();
  const { status } = useAccount();
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (!connectModalOpen || status !== 'connecting') {
      clearTimeout(timerRef.current);
      return;
    }

    timerRef.current = setTimeout(async () => {
      await wagmiReconnect(config);
    }, 5000);

    return () => clearTimeout(timerRef.current);
  }, [connectModalOpen, status, config]);

  return null;
};

export const RainwbowKitWrapper: React.FC<RainwbowKitWrapperProps> = ({ children }) => {
  const { language } = useTranslation();

  return (
    <RainbowKitProvider
      locale={language.bcp47Tag as Locale}
      appInfo={{
        appName: 'Venus',
      }}
      theme={rkTheme}
    >
      <ConnectionRecovery />
      {children}
    </RainbowKitProvider>
  );
};
